import { createRouter, createWebHistory } from 'vue-router'

import { bootSession, isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'
import { useCompanyStore } from '@/stores/company'

declare module 'vue-router' {
    interface RouteMeta {
        layout?:          'Default' | 'Blank'
        requiresAuth?:    boolean
        requiresCompany?: boolean
        admin?:           boolean
    }
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        { path: '/', redirect: '/avtomati' },
        {
            path: '/prijava', name: 'login',
            component: () => import('@/views/auth/Login.vue'),
            meta: { layout: 'Blank', requiresAuth: false },
        },
        {
            path: '/prijava/povezava', name: 'login-link',
            component: () => import('@/views/auth/LoginLink.vue'),
            meta: { layout: 'Blank', requiresAuth: false },
        },
        {
            path: '/povabilo', name: 'invitation',
            component: () => import('@/views/auth/Invitation.vue'),
            meta: { layout: 'Blank', requiresAuth: false },
        },
        {
            path: '/pogoji', name: 'terms',
            component: () => import('@/views/auth/Terms.vue'),
            meta: { layout: 'Blank', requiresAuth: false },
        },
        {
            path: '/dobrodoslica', name: 'welcome',
            component: () => import('@/views/auth/Welcome.vue'),
            meta: { layout: 'Blank', requiresAuth: true },
        },
        {
            path: '/pogoji/sprejem', name: 'terms-accept',
            component: () => import('@/views/auth/TermsAccept.vue'),
            meta: { layout: 'Blank', requiresAuth: true },
        },
        {
            path: '/podjetje', name: 'company-setup',
            component: () => import('@/views/auth/CompanySetup.vue'),
            meta: { layout: 'Blank', requiresAuth: true },
        },
        {
            path: '/avtomati', name: 'machines',
            component: () => import('@/views/machine/Machines.vue'),
            meta: { requiresAuth: true, requiresCompany: true },
        },
        {
            path: '/uvoz', name: 'imports',
            component: () => import('@/views/import/Imports.vue'),
            meta: { requiresAuth: true, requiresCompany: true },
        },
        {
            path: '/uvoz/:id', name: 'import',
            component: () => import('@/views/import/Import.vue'),
            meta: { requiresAuth: true, requiresCompany: true },
        },
        {
            path: '/revizija', name: 'audit',
            component: () => import('@/views/audit/Audits.vue'),
            meta: { requiresAuth: true, requiresCompany: true },
        },
        {
            path: '/nastavitve/:tab?', name: 'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta: { requiresAuth: true, requiresCompany: true },
        },
        {
            path: '/varnost', name: 'security',
            component: () => import('@/views/security/Security.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/admin/uporabniki', name: 'admin-users',
            component: () => import('@/views/admin/AdminUsers.vue'),
            meta: { requiresAuth: true, admin: true },
        },
        {
            path: '/admin/podjetja', name: 'admin-companies',
            component: () => import('@/views/admin/AdminCompanies.vue'),
            meta: { requiresAuth: true, admin: true },
        },
        {
            path: '/admin/nastavitve', name: 'admin-settings',
            component: () => import('@/views/admin/AdminSettings.vue'),
            meta: { requiresAuth: true, admin: true },
        },
        {
            path: '/admin/revizija', name: 'admin-audit',
            component: () => import('@/views/admin/AdminAudit.vue'),
            meta: { requiresAuth: true, admin: true },
        },
        { path: '/:pathMatch(.*)*', redirect: '/avtomati' },
    ],
})

router.beforeEach(async (to) => {
    await bootSession()
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    // Every route is protected unless it opts out with `requiresAuth: false` (fail closed).
    if (to.meta.requiresAuth === false) {
        if (loggedIn && to.name === 'login') return { name: 'machines' }
        return true
    }

    if (!loggedIn) {
        return { name: 'login', query: to.fullPath !== '/avtomati' ? { redirect: to.fullPath } : {} }
    }

    const me = appStore.me
    if (me?.needs_name && to.name !== 'welcome') return { name: 'welcome' }
    if (!me?.needs_name && me?.needs_terms && to.name !== 'terms-accept') return { name: 'terms-accept' }
    if (to.name === 'welcome' || to.name === 'terms-accept') {
        if (!me?.needs_name && !me?.needs_terms) return { name: 'machines' }
        return true
    }

    if (to.meta.admin && !appStore.isAdmin) return { name: 'machines' }

    const companyStore = useCompanyStore()
    if (!companyStore.loaded) await companyStore.init()

    if (to.meta.requiresCompany && !companyStore.company) return { name: 'company-setup' }
    return true
})

export default router
